import {parse} from 'query-string'
import * as redux from 'redux'
import * as ReduxObservable from 'redux-observable'
import 'rxjs/add/operator/filter'
import 'rxjs/add/operator/map'
import 'rxjs/add/operator/mapTo'
import * as App from '../../app/constants'
import * as AppTypes from '../../app/types'
import * as Flash from '../../components/flash-message'
import * as Menu from '../../components/menu'
import * as RootConstants from '../../constants'
import * as CommunityConfig from '../../pages/community-config'
import * as Request from '../../request'
import {ApplicationState} from '../../rootReducer'
import * as Router from '../../router'
import * as Actions from './actions'
import * as Constants from './constants'
import * as Utils from './utils'

export const handleValidate = (
  action$: ReduxObservable.ActionsObservable<redux.Action>,
  store: redux.Store<ApplicationState>,
  {history, localStorage}: AppTypes.Dependencies,
) => action$.ofType(Request.errorConstant(Constants.VALIDATE_TOKEN))
  .filter(() => !history.location.pathname.startsWith(Menu.AUTH_LOGIN_VALIDATION_URL))
  .do(() => localStorage.setItem(RootConstants.REDIRECT_TO,
    history.location.pathname === Menu.AUTH_LOGIN_URL ? '/' : history.location.pathname))
  .do(() => localStorage.removeItem(RootConstants.ACCESS_TOKEN))
  .mapTo(Router.push(Menu.AUTH_LOGIN_URL))

export const validateNavigate = (
  action$: ReduxObservable.ActionsObservable<redux.Action>,
  store: redux.Store<ApplicationState>,
  {history, localStorage}: AppTypes.Dependencies,
) => action$.ofType(App.INITIALIZING_APPLICATION, Router.LOCATION_CHANGE)
  .filter(() => !Boolean(localStorage.getItem(RootConstants.ACCESS_TOKEN)))
  .filter(() => Utils.pageNeedsAuthentication(history))
  .map(() => localStorage.setItem(RootConstants.REDIRECT_TO,
    history.location.pathname === Menu.AUTH_LOGIN_URL ? '/' : history.location.pathname))
  .mapTo(Router.push(Menu.AUTH_LOGIN_URL))

// Two Epics to validate the token - depends on the presence of SELECTED_COMMUNITY in the local storage.
// If its present, that means we want to trigger off of a SELECT_COMMUNITY, which covers 2 use cases:
// a) Initial page open (when authenticated)
// b) Changing the community
// If its not present, that means we need to trigger off of SELECT_COMMUNITY, which covers the
// case of us logging in for the first time.  Login triggers getting the communities,
// and we get the first one and select it.  Then we want to validate the token against that one.
export const validateTokenAgainstCommunityAfterLogin = (
  action$: ReduxObservable.ActionsObservable<redux.Action>,
  store: redux.Store<ApplicationState>,
  {history, localStorage}: AppTypes.Dependencies,
) => action$.ofType(App.INITIALIZING_APPLICATION)
  .filter(() => Utils.pageNeedsAuthentication(history))
  .filter(() => Boolean(localStorage.getItem(RootConstants.ACCESS_TOKEN)))
  .filter(() => !Boolean(localStorage.getItem(App.SELECTED_COMMUNITY)))
  .map(() => Actions.validateToken(store.getState().app.selectedCommunity))

export const validateTokenAgainstCommunityAfterSettingCommunity = (
  action$: ReduxObservable.ActionsObservable<redux.Action>,
  store: redux.Store<ApplicationState>,
  {history, localStorage}: AppTypes.Dependencies,
) => action$.ofType(App.SELECT_COMMUNITY)
  .filter(() => Utils.pageNeedsAuthentication(history))
  .filter(() => Boolean(localStorage.getItem(RootConstants.ACCESS_TOKEN)))
  .filter(() => Boolean(localStorage.getItem(App.SELECTED_COMMUNITY)))
  .map(() => Actions.validateToken(store.getState().app.selectedCommunity))

// This epic exists to support Cognito login form directly for backwards compatibility.
// Login now runs through oauth-login epics.
export const handleLogin = (
  action$: ReduxObservable.ActionsObservable<any>,
  store: redux.Store<ApplicationState>,
  {localStorage, history}: AppTypes.Dependencies,
) => action$.ofType(App.INITIALIZING_APPLICATION)
  .filter(() => history.location.pathname.startsWith(Menu.AUTH_COGNITO_LOGIN_URL))
  .map(() => parse(history.location.search))
  .map(({access_token}) => localStorage.setItem(RootConstants.ACCESS_TOKEN, access_token))
  .map(() => localStorage.getItem(RootConstants.REDIRECT_TO))
  .do(() => localStorage.removeItem(RootConstants.REDIRECT_TO))
  .map((redirectTo) => Router.redirect(redirectTo || '/'))

export const handleLogout = (
  action$: ReduxObservable.ActionsObservable<redux.Action>,
  store: redux.Store<ApplicationState>,
  {history, localStorage}: AppTypes.Dependencies,
) => action$.ofType(App.INITIALIZING_APPLICATION)
  .filter(() => history.location.pathname.startsWith(Menu.AUTH_LOGOUT_URL))
  .filter(() => Boolean(localStorage.getItem(RootConstants.ACCESS_TOKEN)))
  .map(() => localStorage.removeItem(RootConstants.ACCESS_TOKEN))
  .do(() => localStorage.removeItem(App.SELECTED_COMMUNITY))
  .mapTo(Router.redirect(Menu.AUTH_LOGIN_URL))

export const noCommunitiesNotification = (
  action$: ReduxObservable.ActionsObservable<redux.Action>,
  store: redux.Store<ApplicationState>,
  {history, localStorage}: AppTypes.Dependencies,
) => action$.ofType(Request.successConstant(CommunityConfig.FETCH_COMMUNITIES))
  .filter((action: CommunityConfig.RecieivingCommunitiesSuccessAction) =>
    (action.payload.response || []).length === 0)
  .mapTo(Flash.showFlashError('Currently not authorized for any communities.'))

export const epic = ReduxObservable.combineEpics(
  handleValidate,
  handleLogin,
  handleLogout,
  noCommunitiesNotification,
  validateTokenAgainstCommunityAfterLogin,
  validateTokenAgainstCommunityAfterSettingCommunity,
  validateNavigate,
)



// WEBPACK FOOTER //
// ./src/pages/auth/epic.ts